import { StateCreator } from "zustand";

/**
 * Public profile sheet slice state.
 *
 * NOTE: This slice only holds client-only UI state (sheet open/closed).
 * Profile data comes from the server via React Query.
 */
export type PublicProfileSliceState = {
  /** Whether the public profile preview sheet is open */
  isPublicProfileSheetOpen: boolean;
  /** User ID to display in the profile sheet (null = own profile) */
  publicProfileSelectedUserId: number | null;
};

/**
 * Public profile sheet slice actions.
 */
export type PublicProfileSliceActions = {
  openPublicProfileSheet: (userId?: number) => void;
  closePublicProfileSheet: () => void;
  togglePublicProfileSheet: () => void;
};

/**
 * Full public profile slice type.
 */
export type PublicProfileSlice = PublicProfileSliceState &
  PublicProfileSliceActions;

/**
 * Initial state for public profile slice.
 */
export const initialPublicProfileState: PublicProfileSliceState = {
  isPublicProfileSheetOpen: false,
  publicProfileSelectedUserId: null,
};

/**
 * Creates the public profile slice for Zustand store.
 *
 * @example
 * ```typescript
 * const useStore = create<PublicProfileSlice>()((...a) => ({
 *   ...createPublicProfileSlice(...a),
 * }));
 *
 * // In a component:
 * const isOpen = useStore((s) => s.isPublicProfileSheetOpen);
 * const open = useStore((s) => s.openPublicProfileSheet);
 * ```
 */
export const createPublicProfileSlice: StateCreator<
  PublicProfileSlice,
  [],
  [],
  PublicProfileSlice
> = (set) => ({
  ...initialPublicProfileState,

  openPublicProfileSheet: (userId?: number) =>
    set({ isPublicProfileSheetOpen: true, publicProfileSelectedUserId: userId ?? null }),

  closePublicProfileSheet: () =>
    set({ isPublicProfileSheetOpen: false, publicProfileSelectedUserId: null }),

  togglePublicProfileSheet: () =>
    set((state) => ({
      isPublicProfileSheetOpen: !state.isPublicProfileSheetOpen,
      publicProfileSelectedUserId: state.isPublicProfileSheetOpen ? null : state.publicProfileSelectedUserId,
    })),
});

// --- Selectors ---
export const publicProfile = {
  sheet: (s: PublicProfileSlice) => ({
    isOpen: s.isPublicProfileSheetOpen,
    selectedUserId: s.publicProfileSelectedUserId,
    open: s.openPublicProfileSheet,
    close: s.closePublicProfileSheet,
    toggle: s.togglePublicProfileSheet,
  }),
};
